
import React, { useState, useEffect } from "react";
import SuperAdminLayout from "./SuperAdminLayout";
import { fetchDeliveryStats, fetchDeliveryMapSnapshot, fetchDrivers } from "@/services/superadminApi";
import { CardGlass, Badge, StatCard, Button } from "@/components/ui/PremiumComponents";
import { MapPin, Users, Package, TrendingUp, AlertCircle, RefreshCcw } from 'lucide-react';
import Loader from "@/components/ui/Loader";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Leaflet icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const driverIcon = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/2830/2830312.png',
    iconSize: [25, 25],
    iconAnchor: [12, 12],
    popupAnchor: [0, -10],
});

const restaurantIcon = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/1996/1996068.png',
    iconSize: [25, 25],
    iconAnchor: [12, 12],
    popupAnchor: [0, -10],
});

export default function SuperAdminDeliveryHub() {
    const [stats, setStats] = useState(null);
    const [snapshot, setSnapshot] = useState({ drivers: [], jobs: [] });
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(new Date());

    const loadData = async () => {
        try {
            const [s, m] = await Promise.all([
                fetchDeliveryStats(),
                fetchDeliveryMapSnapshot()
            ]);
            setStats(s);
            setSnapshot(m);
            setLastUpdate(new Date());
        } catch (error) {
            console.error("Error loading delivery hub data", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 30000); // 30s auto-refresh
        return () => clearInterval(interval);
    }, []);

    if (loading && !stats) return <SuperAdminLayout><div className="flex justify-center py-20"><Loader /></div></SuperAdminLayout>;

    return (
        <SuperAdminLayout>
            <div className="space-y-8 animate-in fade-in duration-500">
                <div className="flex justify-between items-end">
                    <div>
                        <h1 className="text-4xl font-black text-white tracking-tight mb-2">Delivery Hub Global</h1>
                        <p className="text-white/50 font-bold uppercase tracking-widest text-xs">Monitoreo de la red logística en tiempo real</p>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-[10px] font-black text-white/30 uppercase tracking-tighter">
                            Última actualización: {lastUpdate.toLocaleTimeString()}
                        </span>
                        <Button onClick={loadData} variant="secondary" size="sm">
                            <RefreshCcw size={16} className="mr-2" />
                            Actualizar
                        </Button>
                    </div>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <StatCard
                        label="Pedidos Entregados Hoy"
                        value={stats?.delivered_today || 0}
                        icon={<Package />}
                        color="#10B981"
                    />
                    <StatCard
                        label="Riders Online"
                        value={stats?.online_drivers || 0}
                        icon={<Users />}
                        color="#3B82F6"
                    />
                    <StatCard
                        label="Earnings Plataforma"
                        value={`€${stats?.platform_income_today || 0}`}
                        icon={<TrendingUp />}
                        color="#8B5CF6"
                    />
                    <StatCard
                        label="Alertas SLA"
                        value={stats?.sla?.pending_over_5min + stats?.sla?.assigned_over_20min || 0}
                        icon={<AlertCircle />}
                        theme={stats?.sla?.pending_over_5min > 0 ? "danger" : "default"}
                        color="#EF4444"
                    />
                </div>

                {/* Map & Alerts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <CardGlass className="lg:col-span-2 p-0 overflow-hidden h-[600px]">
                        <MapContainer
                            key={(() => {
                                const points = [
                                    ...(snapshot.jobs?.filter(j => j.restaurant_lat).map(j => [j.restaurant_lat, j.restaurant_lng]) || []),
                                    ...(snapshot.drivers?.filter(d => d.lat).map(d => [d.lat, d.lng]) || [])
                                ];
                                return points.length > 0 ? points[0].join(',') : '40.4168,-3.7038';
                            })()}
                            center={(() => {
                                const points = [
                                    ...(snapshot.jobs?.filter(j => j.restaurant_lat).map(j => [j.restaurant_lat, j.restaurant_lng]) || []),
                                    ...(snapshot.drivers?.filter(d => d.lat).map(d => [d.lat, d.lng]) || [])
                                ];
                                if (points.length === 0) return [40.4168, -3.7038];
                                const sum = points.reduce((acc, p) => [acc[0] + p[0], acc[1] + p[1]], [0, 0]);
                                return [sum[0] / points.length, sum[1] / points.length];
                            })()}
                            zoom={(() => {
                                const points = [
                                    ...(snapshot.jobs?.filter(j => j.restaurant_lat).map(j => [j.restaurant_lat, j.restaurant_lng]) || []),
                                    ...(snapshot.drivers?.filter(d => d.lat).map(d => [d.lat, d.lng]) || [])
                                ];
                                return points.length > 0 ? 11 : 13;
                            })()}
                            style={{ height: '100%', width: '100%' }}
                            zoomControl={false}
                        >
                            <TileLayer
                                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                                attribution='&copy; OpenStreetMap &copy; CARTO'
                            />
                            {/* Restaurants from jobs */}
                            {snapshot.jobs?.map(job => job.restaurant_lat && (
                                <Marker
                                    key={`rest-${job.job_id}`}
                                    position={[job.restaurant_lat, job.restaurant_lng]}
                                    icon={restaurantIcon}
                                >
                                    <Popup>
                                        <div className="p-1">
                                            <p className="font-bold text-xs">{job.restaurant_name}</p>
                                            <p className="text-[10px] text-gray-500">Origen de pedido #{job.order_id}</p>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                            {/* Drivers online */}
                            {snapshot.drivers?.map(d => d.lat && (
                                <Marker
                                    key={d.driver_id}
                                    position={[d.lat, d.lng]}
                                    icon={driverIcon}
                                >
                                    <Popup>
                                        <div className="p-1">
                                            <p className="font-bold text-xs">{d.name}</p>
                                            <p className="text-[10px] text-gray-500">{d.status?.toUpperCase()}</p>
                                            {d.current_job_id && (
                                                <Badge status="info" className="mt-1">Ocupado</Badge>
                                            )}
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    </CardGlass>

                    <div className="space-y-6">
                        <CardGlass title="Alertas de Red">
                            <div className="space-y-4">
                                {stats?.sla?.pending_over_5min > 0 && (
                                    <div className="p-4 bg-danger/10 border border-danger/20 rounded-2xl flex gap-4">
                                        <AlertCircle className="text-danger flex-shrink-0" size={20} />
                                        <div>
                                            <p className="text-sm font-black text-white">{stats.sla.pending_over_5min} Pedidos críticos</p>
                                            <p className="text-xs text-danger/80">Más de 5 min sin ser tomados por un rider.</p>
                                        </div>
                                    </div>
                                )}
                                {stats?.sla?.assigned_over_20min > 0 && (
                                    <div className="p-4 bg-warning/10 border border-warning/20 rounded-2xl flex gap-4">
                                        <AlertCircle className="text-warning flex-shrink-0" size={20} />
                                        <div>
                                            <p className="text-sm font-black text-white">{stats.sla.assigned_over_20min} Envíos lentos</p>
                                            <p className="text-xs text-warning/80">Más de 20 min en estado asignado.</p>
                                        </div>
                                    </div>
                                )}
                                {(!stats?.sla?.pending_over_5min && !stats?.sla?.assigned_over_20min) && (
                                    <div className="py-10 text-center">
                                        <p className="text-xs font-bold text-white/30 tracking-widest uppercase">Red saludable</p>
                                        <div className="text-3xl mt-2">✅</div>
                                    </div>
                                )}
                            </div>
                        </CardGlass>

                        <CardGlass title="Riders Activos">
                            <div className="space-y-3">
                                {snapshot.drivers?.slice(0, 5).map(d => (
                                    <div key={d.driver_id} className="flex justify-between items-center p-3 glass-effect rounded-xl border border-white/5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-black">
                                                {d.name.charAt(0)}
                                            </div>
                                            <div>
                                                <p className="text-xs font-bold text-white tracking-tight">{d.name}</p>
                                                <p className="text-[9px] text-white/40 uppercase">{d.vehicle_type || 'Moto'}</p>
                                            </div>
                                        </div>
                                        <Badge status={d.status === 'online' ? 'success' : 'default'} className="!text-[8px]">
                                            {d.status?.toUpperCase()}
                                        </Badge>
                                    </div>
                                ))}
                                {snapshot.drivers?.length === 0 && (
                                    <p className="text-center py-4 text-xs text-white/20">No hay riders en línea</p>
                                )}
                            </div>
                        </CardGlass>
                    </div>
                </div>
            </div>
        </SuperAdminLayout>
    );
}
